/* eslint-disable react/jsx-one-expression-per-line */
import { useState } from "react";
import moment from "moment";
import { DateIcon, MagnetReleaseIcon, TimeIcon } from "components/atoms/Icon";
import {
  isObjectEmpty,
  isReleaseDraft,
  isReleaseEnded,
  isReleaseInSchedulingState,
  isReleaseOnSale,
  isReleaseScheduled,
} from "utils/helpers";
import Table from "components/molecules/Table";
import ManageReleaseModal from "./ManageReleaseModal";
import Badge from "../../../../../components/atoms/badge";
import { DATE_FORMAT } from "../../../../../constants";
import EndReleaseModal from "./EndReleaseModal";
import ReleaseTableDropDownMenu from "./ReleaseTableDropdownMenu";
import PrivateReleaseModal from "./PrivateReleaseModal";
import ExclusiveReleaseModal from "./ExclusiveReleaseModal";
import DeleteDraftReleaseModal from "./DeleteDraftReleaseModal";
import ConfirmPublishReleaseModal from "./ConfirmPublishReleaseModal";

interface IProps {
  data: Array<Release>;
  magnetMaxSupply: number;
  isManageMagnetReleaseModalOpen: boolean;
  setIsManageMagnetReleaseModalOpen: (open: boolean) => void;
  onReleaseEditClick: (releaseId: any) => void;
  handleEndRelease: (releaseId: any) => Promise<void>;
  handleDeleteRelease: (releaseId: any) => void;
  handlePublishRelease: (releaseId: any) => void;
  handlePrivateReleaseChange: (releaseId: any, isReleasePrivate: boolean) => void;
  handleWhitelistingChange: (releaseId: number, file?: File) => Promise<any>;
  handleCreateNewRelease: Function;
  isMagnetPublished: boolean;
}

const ReleaseTableHeaderRows = [
  "ACTIVATION",
  "STATUS",
  "START DATE/TIME",
  "END DATE/TIME",
  "SUPPLY FOR RELEASE",
  "TOTAL SOLD",
  "SALE PRICE",
  "ACTIONS",
];

const getStatusBadge = (status: ReleaseStatus) => {
  if (isReleaseDraft(status)) {
    return (
      <Badge
        className="border border-white px-3 py-0.5 text-xxs 2xl:px-5 2xl:py-1 2xl:text-xs"
        forGroundCOlor="#060F1B"
        backgroundColor="white"
        content="Draft"
      />
    );
  }

  if (isReleaseInSchedulingState(status)) {
    return (
      <Badge
        className="border border-white px-3 py-0.5 text-xxs 2xl:px-5 2xl:py-1 2xl:text-xs"
        forGroundCOlor="#060F1B"
        backgroundColor="white"
        content="Scheduling"
      />
    );
  }
  if (isReleaseScheduled(status)) {
    return (
      <Badge
        className="border border-white px-3 py-0.5 text-xxs 2xl:px-5 2xl:py-1 2xl:text-xs"
        forGroundCOlor="#060F1B"
        backgroundColor="white"
        content="Scheduled"
      />
    );
  }
  if (isReleaseOnSale(status)) {
    return (
      <Badge
        className="border border-white px-3 py-0.5 text-xxs 2xl:px-5 2xl:py-1 2xl:text-xs"
        forGroundCOlor="#060F1B"
        backgroundColor="white"
        content="On Sale"
      />
    );
  }

  if (isReleaseEnded(status)) {
    return (
      <Badge
        className="border border-white px-3 py-0.5 text-xxs 2xl:px-5 2xl:py-1 2xl:text-xs"
        forGroundCOlor="white"
        backgroundColor="#060F1B"
        content="Sale Ended"
      />
    );
  }
  return null;
};

function ReleaseTable({
  data: releasesData,
  magnetMaxSupply,
  onReleaseEditClick,
  handleEndRelease,
  handleDeleteRelease,
  isManageMagnetReleaseModalOpen,
  setIsManageMagnetReleaseModalOpen,
  handlePublishRelease,
  handlePrivateReleaseChange,
  handleWhitelistingChange,
  handleCreateNewRelease,
  isMagnetPublished,
}: IProps) {
  const [manageModalData, setManageModalData] = useState<Release | null>(null);
  const [openEndReleaseModal, setOpenEndReleaseModal] = useState(false);
  const [openConfirmPublishReleaseModal, setOpenConfrimPublishReleaseModal] = useState(false);
  const [openDeleteDraftReleaseModal, setOpenDeleteDraftReleaseModal] = useState(false);
  const [openPrivateReleaseModal, setOpenPrivateReleaseModal] = useState(false);
  const [openExclusiveReleaseModal, setOpenExclusiveReleaseModal] = useState(false);

  const handleManageButtonClick = (releaseToBeEdited: Release) => {
    setManageModalData(() => releaseToBeEdited);
  };

  const onEditReleaseButtonClick = (id: number) => {
    setManageModalData(null);
    onReleaseEditClick(id);
  };

  const data = [...(releasesData || [])].reverse() as Release[];

  return (
    <div className="flex flex-col">
      <div className="mt-2 w-full min-w-[900px] rounded-2xl border border-secondary">
        <Table>
          <Table.Header>
            <Table.Row isHeaderRow>
              {ReleaseTableHeaderRows.map((label) => (
                <Table.HeaderCell label={label} key={label} />
              ))}
            </Table.Row>
          </Table.Header>
          {data.length === 0 ? (
            <Table.EmptyState
              emptyStatefor="Release"
              onClick={() => handleCreateNewRelease()}
              isDisabled={!isMagnetPublished}
              title={isMagnetPublished ? "Create Release" : "Publish Release to create Release"}
            />
          ) : (
            <Table.Body>
              {data.map((release, idx) => (
                <Table.Row key={`release_table_${release.id}`}>
                  {/* Release Name */}
                  <Table.Description>
                    <div className="flex items-center justify-center gap-1 2xl:gap-2">
                      <MagnetReleaseIcon className="h-6 w-6 2xl:h-7 2xl:w-7" />
                      <span className="font-satoshi text-sm font-semibold text-white">
                        {`Release ${data.length - idx}`}
                      </span>
                    </div>
                  </Table.Description>

                  {/* Release Status */}
                  <Table.Description>
                    <div className="flex justify-center">
                      <span className="font-satoshi text-lg font-semibold">{getStatusBadge(release.status)}</span>
                    </div>
                  </Table.Description>

                  {/* Start Date Time */}
                  <Table.Description>
                    <div className="flex justify-center">
                      <div>
                        <span
                          className="flex items-center gap-1 font-satoshi text-xs font-semibold text-white
                   2xl:gap-2 2xl:text-sm"
                        >
                          <DateIcon className="h-4 w-4 2xl:h-5 2xl:w-5" color="text-secondary" />
                          {moment(release.start_time).format(DATE_FORMAT)}
                        </span>
                        <span
                          className="flex items-center gap-1 font-satoshi text-xs font-semibold text-white
                   2xl:gap-2 2xl:text-sm"
                        >
                          <TimeIcon className="h-4 w-4 2xl:h-5 2xl:w-5" color="text-secondary" />

                          {moment(release.start_time).format("hh:mm A")}
                        </span>
                      </div>
                    </div>
                  </Table.Description>

                  {/* End Date Time` */}
                  <Table.Description>
                    <div className="flex justify-center">
                      <div>
                        <span
                          className="flex items-center gap-1 font-satoshi text-xs font-semibold text-white
                   2xl:gap-2 2xl:text-sm"
                        >
                          <DateIcon className="h-4 w-4 2xl:h-5 2xl:w-5" color="text-secondary" />
                          {`${moment(release.end_time).format(DATE_FORMAT)}`}
                        </span>
                        <span
                          className="flex items-center gap-1 font-satoshi text-xs font-semibold text-white
                   2xl:gap-2 2xl:text-sm"
                        >
                          <TimeIcon className="h-4 w-4 2xl:h-5 2xl:w-5" color="text-secondary" />
                          {moment(release.end_time).format("hh:mm A")}
                        </span>
                      </div>
                    </div>
                  </Table.Description>
                  {/* Release Supply vs Magnet Supply */}
                  <Table.Description>
                    <>
                      <span
                        className="font-satoshi text-xs
                   font-semibold text-white 2xl:text-sm"
                      >
                        {release.max_supply}
                      </span>
                      <span
                        className="font-satoshi
                   text-xxs font-semibold  text-white 2xl:text-xs"
                      >
                        /{magnetMaxSupply}
                      </span>
                    </>
                  </Table.Description>
                  {/* Sold/Release Supply  */}
                  <Table.Description>
                    <>
                      <span className="font-satoshi text-xs font-semibold text-white 2xl:text-sm">{release.sold}</span>
                      <span
                        className="font-satoshi
                    text-xxs font-semibold  text-white 2xl:text-xs"
                      >
                        /{release.max_supply}
                      </span>
                    </>
                  </Table.Description>
                  {/* Release Price */}
                  <Table.Description>
                    <span className="font-satoshi text-xs font-semibold text-white 2xl:text-sm">${release.price}</span>
                  </Table.Description>
                  <Table.Description>
                    <ReleaseTableDropDownMenu
                      releaseData={release}
                      onClick={() => handleManageButtonClick(release)}
                      setIsManageMagnetReleaseModalOpen={setIsManageMagnetReleaseModalOpen}
                      setOpenEndReleaseModal={setOpenEndReleaseModal}
                      setOpenDeleteDraftReleaseModal={setOpenDeleteDraftReleaseModal}
                      setOpenConfirmPublishReleaseModal={setOpenConfrimPublishReleaseModal}
                      onEditReleaseButtonClick={onEditReleaseButtonClick}
                      setOpenPrivateReleaseModal={setOpenPrivateReleaseModal}
                      setOpenExclusiveReleaseModal={setOpenExclusiveReleaseModal}
                    />
                  </Table.Description>
                </Table.Row>
              ))}
            </Table.Body>
          )}
        </Table>
        {manageModalData && (
          <ManageReleaseModal
            magnetMaxSupply={magnetMaxSupply}
            handleDeleteRelease={handleDeleteRelease}
            onEditReleaseButtonClick={onEditReleaseButtonClick}
            release={manageModalData}
            open={isManageMagnetReleaseModalOpen}
            setIsManageMagnetReleaseModalOpen={setIsManageMagnetReleaseModalOpen}
            onClose={() => setIsManageMagnetReleaseModalOpen(false)}
            handlePublishRelease={handlePublishRelease}
            setOpenEndReleaseModal={setOpenEndReleaseModal}
          />
        )}
        {openConfirmPublishReleaseModal && (
          <ConfirmPublishReleaseModal
            open={openConfirmPublishReleaseModal}
            handlePublishRelease={async () => {
              await handlePublishRelease(manageModalData?.id);
              setOpenConfrimPublishReleaseModal(false);
            }}
            onClose={() => setOpenConfrimPublishReleaseModal(false)}
            isPrivateRelease={!isObjectEmpty(manageModalData?.event || {})}
            isWhitelistedRelease={Boolean(manageModalData?.is_whitelisted)}
          />
        )}
        {openDeleteDraftReleaseModal && (
          <DeleteDraftReleaseModal
            open={openDeleteDraftReleaseModal}
            handleDeleteDraftRelease={async () => {
              await handleDeleteRelease(manageModalData?.id);
              setOpenDeleteDraftReleaseModal(false);
            }}
            onClose={() => setOpenDeleteDraftReleaseModal(false)}
          />
        )}
        {openEndReleaseModal && (
          <EndReleaseModal
            open={openEndReleaseModal}
            handleEndRelease={async () => {
              await handleEndRelease(manageModalData?.id);
              setOpenEndReleaseModal(false);
            }}
            onClose={() => setOpenEndReleaseModal(false)}
          />
        )}
        {openPrivateReleaseModal && (
          <PrivateReleaseModal
            isPrivateRelease={!isObjectEmpty(manageModalData?.event || {})}
            open={openPrivateReleaseModal}
            handlePrivateReleaseChange={async (isReleasePrivate: boolean) => {
              await handlePrivateReleaseChange(manageModalData?.id, isReleasePrivate);
              setOpenPrivateReleaseModal(false);
            }}
            onClose={() => setOpenPrivateReleaseModal(false)}
            magnetPrice={manageModalData?.price || 0}
          />
        )}
        {openExclusiveReleaseModal && (
          <ExclusiveReleaseModal
            releaseData={manageModalData as Release}
            open={openExclusiveReleaseModal}
            handleMakeReleaseWhitelisted={async (file: File) => {
              await handleWhitelistingChange(manageModalData?.id as number, file);
              setOpenExclusiveReleaseModal(false);
            }}
            handleStopWhitelisting={async () => {
              await handleWhitelistingChange(manageModalData?.id as number);
              setOpenExclusiveReleaseModal(false);
            }}
            onClose={() => setOpenExclusiveReleaseModal(false)}
          />
        )}
      </div>
    </div>
  );
}

export default ReleaseTable;
